import { styleConstants } from '@paywithextend/components'
import { useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import React, { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { connect, useSelector } from 'react-redux'
import { matchPath, Route, Switch } from 'react-router'
import { useIntercom } from 'react-use-intercom'
import AgentWrapper from 'src/agent/AgentWrapper'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import DeleteExpenseReportsPopup from 'src/app//DeleteExpenseReportsPopup/DeleteExpenseReportsPopup'
import AttachMissingInformationExpenseReportPopup from 'src/app/AddMissingInformationExpenseReportPopup/AddMissingInformationExpenseReportPopup'
import APIKeyCreatedPopup from 'src/app/APIProduct/APIKeyCreatedPopup'
import DeleteAPIKeyPopup from 'src/app/APIProduct/DeleteAPIKeyPopup'
import BrandRedirectPopup from 'src/app/BrandRedirectPopup/BrandRedirectPopup'
import BulkCloseVirtualCardsPopup from 'src/app/BulkCloseVirtualCardsPopup/BulkCloseVirtualCardsPopup'
import BulkDeactivateVirtualCardsPopup from 'src/app/BulkDeactivateVirtualCardsPopup/BulkDeactivateVirtualCardsPopup'
import CancelOrgInvitePopup from 'src/app/CancelOrgInvitePopup/CancelOrgInvitePopup'
import Choose2faMethodPopup from 'src/app/Choose2faMethodPopup/Choose2faMethodPopup'
import CreateExpenseReportPopup from 'src/app/CreateExpenseReportPopup/CreateExpenseReportPopup'
import CreateOrgIntegration from 'src/app/CreateOrgIntegration/CreateOrgIntegration'
import DeleteAccountPopup from 'src/app/DeleteAccountPopup/DeleteAccountPopup'
import DeleteExpensePopup from 'src/app/DeleteExpensePopup/DeleteExpensePopup'
import CreateDepartmentPopup from 'src/app/Departments/CreateDepartmentPopup'
import DownloadExpenseReports from 'src/app/DownloadExpenseReports/DownloadExpenseReports'
import DownloadSuccessPopup from 'src/app/DownloadSuccessPopup/DownloadSuccessPopup'
import { useOnboardingDropoffStatus } from 'src/app/Home/useOnboardingDropoffRedirect'
import MarkExpenseReportsPaidPopup from 'src/app/MarkExpenseReportsPaidPopup/MarkExpenseReportsPaidPopup'
import OrgOwnerReassignmentPopup from 'src/app/OrgOwnerReassignmentPopup/OrgOwnerReassignmentPopup'
import RemoveBudgetUserPopup from 'src/app/RemoveBudgetUserPopup/RemoveBudgetUserPopup'
import RequestCompletedPopup from 'src/app/RequestCompletedPopup/RequestCompletedPopup'
import SendReceiptReminderPopup from 'src/app/SendReceiptReminderPopup/SendReceiptReminderPopup'
import SetupOrganization from 'src/app/SetupOrganization/SetupOrganization'
import SubmitExpenseReportPopup from 'src/app/SubmitExpenseReportPopup/SubmitExpenseReportPopup'
import SyncTransactionsPopup from 'src/app/SyncTransactionsPopup/SyncTransactionsPopup'
import Toast from 'src/app/Toast/Toast'
import UploadReceiptsAutoMatchPopup from 'src/app/UploadReceiptsAutoMatchPopup/UploadReceiptsAutoMatchPopup'
import DowngradedToStarterModal from 'src/component-modal/DowngradedToStarterModal/DowngradedToStarterModal'
import AddSecondaryEmailPopup from 'src/components/AddSecondaryEmailPopup/AddSecondaryEmailPopup'
import AlertBars from 'src/components/AlertBars/AlertBars'
import AttachmentPopup from 'src/components/AttachmentPopup/AttachmentPopup'
import BulkUpdateVirtualCardFlyout from 'src/components/BulkUpdateVirtualCardFlyout/BulkUpdateVirtualCardFlyout'
import CoBrandWelcomePopup from 'src/components/CoBrandWelcomePopup/CoBrandWelcomePopup'
import ConfirmOrgExpenseReportDateChangePopup from 'src/components/ConfirmOrgExpenseReportDateChangePopup/ConfirmOrgExpenseReportDateChangePopup'
import CspTopBar from 'src/components/CspTopBar/CspTopBar'
import DevToolsButton from 'src/components/DevToolsButton/DevToolsButton'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import SessionExpiringPopup from 'src/components/SessionExpiringPopup/SessionExpiringPopup'
import Sidenav from 'src/components/Sidenav/Sidenav'
import TooltipPopup from 'src/components/TooltipPopup/TooltipPopup'
import TopBar from 'src/components/TopBar/TopBar'
import { Environments } from 'src/configurations'
import { noRedirectURLs } from 'src/constants'
import useGlobalModal from 'src/context/GlobalModalContext/useGlobalModal'
import { CookieNames, CookieValuePaths } from 'src/cookies'
import { AuthTypes } from 'src/enums'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch, useCookieState } from 'src/hooks'
import { UserFeature } from 'src/models/user.models'
import {
  authedBackgroundAppRoute,
  authedLeftFlyoutAppRoute,
  authedPopupAppRoute,
  authedRightFlyoutAppRoute,
  unauthedAppRoute,
} from 'src/navigation/appRoutes'
import ReimbursementsAppRoute from 'src/navigation/appRoutes/expenses/reimbursements.appRoute'
import APIKeysAppRoute from 'src/navigation/appRoutes/organization/APIKeysAppRoute'
import AutomationSettingsAppRoute from 'src/navigation/appRoutes/organization/automationSettingsAppRoute'
import BusinessRulesAppRoute from 'src/navigation/appRoutes/organization/businessRules.appRoute'
import CardFeedsAppRoute from 'src/navigation/appRoutes/organization/cardFeeds.appRoute'
import DepartmentsAppRoute from 'src/navigation/appRoutes/organization/departments.appRoute'
import ExpenseCategoriesAppRoute from 'src/navigation/appRoutes/organization/expenseCategories.appRoute'
import ExpenseRequirementsAppRoute from 'src/navigation/appRoutes/organization/expenseRequirements.appRoute'
import ManageSubscriptionAppRoute from 'src/navigation/appRoutes/organization/manageSubscription.appRoute'
import OrganizationInfoAppRoute from 'src/navigation/appRoutes/organization/organizationInfo.appRoute'
import OrganizationIntegrationsAppRoute from 'src/navigation/appRoutes/organization/organizationIntegrations.appRoute'
import OrgSettingsReimbursementsAppRoute from 'src/navigation/appRoutes/organization/reimbursements.appRoute'
import SSOSettingsAppRoute from 'src/navigation/appRoutes/organization/ssoSettings.appRoute'
import { Rdrct } from 'src/navigation/Lnk'
import {
  homeUrl,
  onboardingCardUrl,
  onboardingUrl,
  orgInviteDecisionUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import {
  useQueryOrganizationSaasTier,
  useQueryOrganizationUsages,
} from 'src/queries/organizations/saas.queries'
import {
  createSetIsAddDeptPopupOpen,
  createSetIsAPIKeyCreatedPopupOpen,
  createSetIsDeleteAPIKeyPopupOpen,
  createSetIsWhatsNewPopupOpen,
} from 'src/redux/actions/app.actions'
import {
  deleteCognitoSignout,
  deleteCspEmulatedSessions,
  deleteSignout,
} from 'src/redux/actions/auth.actions'
import { getUserOrganizationInvites } from 'src/redux/actions/data/organizations.actions'
import { RootState } from 'src/redux/createRootReducer'
import {
  selectAllStatisticsLoadedFromState,
  selectConfirmOrgExpenseReportDateChangePopupPropsFromState,
  selectIsAddDepartmentPopupOpenFromState,
  selectIsAddMissingInformationExpenseReportPopupOpenFromState,
  selectIsAddSecondaryEmailPopupOpenFromState,
  selectIsAPICreatedPopupOpenFromState,
  selectIsAttachmentPopupOpenFromState,
  selectIsBrandRedirectPopupOpenFromState,
  selectIsBrandSpecificWelcomePopupOpenFromState,
  selectIsBulkCloseVirtualCardsPopupOpenFromState,
  selectIsBulkDeactivateVirtualCardsPopupOpenFromState,
  selectIsBulkUpdateVirtualCardsFlyoutOpenFromState,
  selectIsCancelOrgInvitePopupOpenFromState,
  selectIsChoose2faMethodPopupOpenFromState,
  selectIsCreateDeleteExpensePopupOpenFromState,
  selectIsCreateDeleteExpenseReportsPopupOpenFromState,
  selectIsCreateExpenseReportPopupOpenFromState,
  selectIsCreateOrgIntegrationOpenFromState,
  selectIsDeleteAccountPopupOpenFromState,
  selectIsDeleteAPIKeyPopupOpenFromState,
  selectIsDownloadExpenseReportsPaidPopupOpenFromState,
  selectIsDownloadSuccessPopupOpenFromState,
  selectIsMarkExpenseReportsPaidPopupOpenFromState,
  selectIsOrgOwnerReassignmentPopupOpenFromState,
  selectIsRemoveBudgetUserPopupOpenFromState,
  selectIsRequestCompletedFromState,
  selectIsSendReceiptReminderPopupOpenFromState,
  selectIsSessionExpiringPopupOpenFromState,
  selectIsSetupOrganizationPopupOpenFromState,
  selectIsSubmitExpenseReportPopupOpenFromState,
  selectIsSyncTransactionsPopupOpenFromState,
  selectIsTooltipPopupOpenFromState,
  selectisUploadReceiptsAutoMatchPopupOpenFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import {
  selectAuthTypeFromState,
  selectIsCspAuthenticatedFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectIsBetaOrgAgent,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectLocationFromState,
} from 'src/redux/selectors/router.selectors'
import {
  selectAppName,
  selectCoBrandName,
  selectEnvironmentFromState,
} from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const Authed: React.FC = (): React.ReactElement => {
  const location = useSelector(selectLocationFromState)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  const isDeleteAccountPopOpen = useSelector(
    selectIsDeleteAccountPopupOpenFromState,
  )
  const isOrgOwnerReassignmentPopupOpen = useSelector(
    selectIsOrgOwnerReassignmentPopupOpenFromState,
  )
  const isCancelOrgInvitePopupOpen = useSelector(
    selectIsCancelOrgInvitePopupOpenFromState,
  )
  const isCreateOrgIntegrationOpen = useSelector(
    selectIsCreateOrgIntegrationOpenFromState,
  )
  const isSendReceiptReminderPopupOpen = useSelector(
    selectIsSendReceiptReminderPopupOpenFromState,
  )
  const isCreateExpenseReportPopupOpen = useSelector(
    selectIsCreateExpenseReportPopupOpenFromState,
  )
  const isMarkExpenseReportsPaidPopupOpen = useSelector(
    selectIsMarkExpenseReportsPaidPopupOpenFromState,
  )
  const isDownloadExpenseReportsPaidPopupOpen = useSelector(
    selectIsDownloadExpenseReportsPaidPopupOpenFromState,
  )
  const isDownloadSuccessPopupOpen = useSelector(
    selectIsDownloadSuccessPopupOpenFromState,
  )
  const isDeleteExpensePopupOpen = useSelector(
    selectIsCreateDeleteExpensePopupOpenFromState,
  )
  const isDeleteExpenseReportsPopupOpen = useSelector(
    selectIsCreateDeleteExpenseReportsPopupOpenFromState,
  )
  const isSubmitExpenseReportPopupOpen = useSelector(
    selectIsSubmitExpenseReportPopupOpenFromState,
  )
  const isAddMissingInformationExpenseReportPopupOpen = useSelector(
    selectIsAddMissingInformationExpenseReportPopupOpenFromState,
  )
  const isAttachmentPopupOpen = useSelector(
    selectIsAttachmentPopupOpenFromState,
  )
  const isUploadReceiptsAutoMatchPopupOpen = useSelector(
    selectisUploadReceiptsAutoMatchPopupOpenFromState,
  )
  const isTooltipPopupOpen = useSelector(selectIsTooltipPopupOpenFromState)
  const isSessionExpiringPopupOpen = useSelector(
    selectIsSessionExpiringPopupOpenFromState,
  )
  const isRemoveBudgetUserPopupOpen = useSelector(
    selectIsRemoveBudgetUserPopupOpenFromState,
  )
  const isRequestCompletedPopupOpen = useSelector(
    selectIsRequestCompletedFromState,
  )
  const isBulkDeactivateVirtualCardsPopupOpen = useSelector(
    selectIsBulkDeactivateVirtualCardsPopupOpenFromState,
  )
  const isBulkCloseVirtualCardsPopupOpen = useSelector(
    selectIsBulkCloseVirtualCardsPopupOpenFromState,
  )
  const user = useSelector(selectUserListModelFromState)
  const org = useSelector(selectUserExplicitOrganizationListModelFromState)
  const orgId = org?.id
  const appName = useSelector(selectAppName)
  const coBrandName = useSelector(selectCoBrandName)
  const isBrandSpecificWelcomePopupOpen = useSelector(
    selectIsBrandSpecificWelcomePopupOpenFromState,
  )
  const authType = useSelector(selectAuthTypeFromState)
  const isSyncTrasactionsPopupOpen = useSelector(
    selectIsSyncTransactionsPopupOpenFromState,
  )
  const isSetupOrganizationPopupOpen = useSelector(
    selectIsSetupOrganizationPopupOpenFromState,
  )
  const isBulkUpdateVirtualCardsFlyoutOpenFromState = useSelector(
    selectIsBulkUpdateVirtualCardsFlyoutOpenFromState,
  )
  const isAddSecondaryEmailPopupOpen = useSelector(
    selectIsAddSecondaryEmailPopupOpenFromState,
  )
  const isChoose2faMethodPopupOpen = useSelector(
    selectIsChoose2faMethodPopupOpenFromState,
  )
  const isCreateDepartmentPopupOpen = useSelector(
    selectIsAddDepartmentPopupOpenFromState,
  )
  const isAPIKeyCreatedPopupOpen = useSelector(
    selectIsAPICreatedPopupOpenFromState,
  )
  const isDeleteAPIKeyPopupOpen = useSelector(
    selectIsDeleteAPIKeyPopupOpenFromState,
  )
  const confirmOrgExpenseReportDateChangePopupProps = useSelector(
    selectConfirmOrgExpenseReportDateChangePopupPropsFromState,
  )
  const env = useSelector(selectEnvironmentFromState)
  const { show_pnc_cobrand: showPncCoBrand, loadedConfig } = useSelector(
    selectFeatureFlaggerFromState,
  )
  const queryClient = useQueryClient()
  const { analytics } = useAnalyticsContext()
  const intercom = useIntercom()
  const { status: orgFFStatus } = useQueryOrganizationFeatureFlags({
    orgId,
    enabled: orgId !== undefined,
  })

  const isBrandRedirectPopupOpen = useSelector(
    selectIsBrandRedirectPopupOpenFromState,
  )
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const isAgentBetaOrg = useSelector(selectIsBetaOrgAgent)

  const dispatch = useBackgroundDispatch()
  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)
  const globalModal = useGlobalModal()

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsUserOrViewerMeCountStatistics,
      appStatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCountStatistics,
    ])
  }, [dispatchStatistics])

  // remove all stored data when user is de-authed
  useEffect(() => {
    return () => queryClient.removeQueries()
  }, [])

  useEffect(() => {
    intercom.boot({
      name: user?.fullNameOrEmail, // Full name
      email: user?.email, // the email for your user
      createdAt: user?.createdAt, // Signup date as a Unix timestamp
    })
    return () => {
      intercom.hide()
      intercom.shutdown()
    }
  }, [])

  const dispatchDeleteSignout = (): void => {
    if (authType === AuthTypes.auth0) {
      dispatch(deleteSignout(false, analytics, true, undefined))
    } else {
      dispatch(deleteCognitoSignout(false, analytics, true))
    }
    dispatch(deleteCspEmulatedSessions(false, false, analytics))
  }

  const dispatchDeleteCspEmulatedSessions = (
    shouldDeleteSignout: boolean,
  ): void => {
    dispatch(deleteCspEmulatedSessions(false, shouldDeleteSignout, analytics))
  }

  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)

  // EX-39432 — read the homepage drop-off status here so we can suppress the
  // sidebar/header chrome during the brief window between landing on /home
  // and the redirect to /onboarding/card firing. The hook itself is read-only
  // (the imperative redirect lives in `useOnboardingDropoffRedirect` over in
  // Home.tsx); React Query dedupes the underlying /statistics request so this
  // extra caller is free.
  const { isResumingOnboarding } = useOnboardingDropoffStatus()

  const notInOnboardingFlow =
    location.pathname !== onboardingUrl() &&
    location.pathname !== onboardingCardUrl() &&
    calculatedBackgroundLocation.pathname !== onboardingUrl() &&
    calculatedBackgroundLocation.pathname !== onboardingCardUrl() &&
    location.pathname !== orgInviteDecisionUrl() &&
    calculatedBackgroundLocation.pathname !== orgInviteDecisionUrl() &&
    !isResumingOnboarding

  const {
    cookieValue: coBrandWelcomePopupOpen,
    updateCookieValue: setCoBrandWelcomePopupOpen,
  } = useCookieState(
    CookieNames.Authed,
    CookieValuePaths.coBrandWelcomePopupOpen,
    true,
  )

  // what's new display logic
  const { cookieValue: shouldOpenWhatsNew } = useCookieState(
    CookieNames.App,
    CookieValuePaths.shouldShowRulesWhatsNew,
    true,
  )
  const { VIEW_AMEX_CPG_INTEGRATION: viewAmexCPGIntegration } =
    useQueryUserFeatures([UserFeature.VIEW_AMEX_CPG_INTEGRATION]).data || {}
  useEffect(() => {
    const RELEASE_DATE = '2025-03-18'
    const showWhatsNew =
      shouldOpenWhatsNew && // check cookie
      viewAmexCPGIntegration && // user can see the connection
      !organization?.physicalClearingsEnabled // org is not currently connected to CPG
    // note: to prevent this for showing in orgs who turn this off,
    // the cookie is also updated when a user connects/disconnects from CPG
    if (showWhatsNew && moment(user?.createdAt).isBefore(RELEASE_DATE)) {
      dispatch(createSetIsWhatsNewPopupOpen(true))
    }
  }, [user, shouldOpenWhatsNew])

  const { data: orgUsages } = useQueryOrganizationUsages({
    orgId: orgId,
  })

  const { data: currentPlan } = useQueryOrganizationSaasTier({
    orgId: orgId,
    enabled: !!orgId,
  })

  const {
    cookieValue: shouldShowDowngradedToStarterModal,
    updateCookieValue: setShouldShowDowngradedToStarterModal,
  } = useCookieState(
    CookieNames.App,
    CookieValuePaths.shouldShowDowngradedToStarterModal,
    true,
  )
  useEffect((): void => {
    if (user === undefined || orgUsages === undefined) {
      return
    }

    // This modal should only show for STARTER tier users who exceed their seat limits.
    // Without the tier check, PRO users who have more seats than they pay for would
    // incorrectly see the "downgraded to starter" modal.
    const showDowngradeMessage =
      user.hasOrgAdminPermissions &&
      currentPlan?.tier === 'STARTER' &&
      (orgUsages.features.ORGANIZATION_TOTAL_SIZE?.withinLimit === false ||
        orgUsages.features.GUEST_ROLE?.withinLimit === false) &&
      shouldShowDowngradedToStarterModal

    if (showDowngradeMessage) {
      globalModal.showGlobalModal(
        <DowngradedToStarterModal
          isOpen
          onClose={() => {
            setShouldShowDowngradedToStarterModal(false)
            setTimeout(() => {
              globalModal.closeGlobalModal()
            }, 100)
          }}
        />,
      )
    }
  }, [
    user,
    orgUsages,
    currentPlan,
    shouldShowDowngradedToStarterModal,
    setShouldShowDowngradedToStarterModal,
  ])

  // get users organization invites
  useEffect((): void => {
    dispatch(
      getUserOrganizationInvites({
        sortDirection: 'ASC',
        sortField: 'createdAt',
      }),
    )
  }, [])

  const offSetDevTools =
    !noRedirectURLs?.includes(location.pathname) &&
    !!document.getElementById('userpilot-checklist')
  const inLoadingState = !(
    statisticsLoaded.creditCardsUserOrViewerMeCountStatisticsLoaded &&
    statisticsLoaded.creditCardsActiveCanIssueVirtualCardsCountStatisticsLoaded &&
    // ignore that org feature flags are undefined if the user is in the onboarding flow
    (orgFFStatus !== 'pending' || !notInOnboardingFlow) &&
    loadedConfig
  )

  const updateAuthedBackgroundAppRoute = useMemo(() => {
    return {
      ...authedBackgroundAppRoute,
      subRoutes: [
        ...authedBackgroundAppRoute.subRoutes,
        ExpenseCategoriesAppRoute,
        DepartmentsAppRoute,
        CardFeedsAppRoute,
        OrganizationIntegrationsAppRoute,
        BusinessRulesAppRoute,
        ExpenseRequirementsAppRoute,
        OrgSettingsReimbursementsAppRoute,
        ReimbursementsAppRoute,
        OrganizationInfoAppRoute,
        ManageSubscriptionAppRoute,
        SSOSettingsAppRoute,
        APIKeysAppRoute,
        AutomationSettingsAppRoute,
      ],
    }
  }, [])

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        min-height: 100vh;
        width: 100%;
        flex-direction: column;
      `}
    >
      <Helmet>
        <title>{`${appName}`}</title>
        <meta
          name="description"
          content={`Log into ${appName} to create, send, and manage virtual cards for all your business payments.`}
        />
      </Helmet>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          width: 100%;
          flex-grow: 2;
        `}
      >
        {isCspAuthenticated && (
          <CspTopBar
            deleteSignout={dispatchDeleteSignout}
            deleteCspEmulatedSessions={dispatchDeleteCspEmulatedSessions}
          />
        )}
        {/* only render alertbars and sidenav if not in onboarding flow (may need to refactor how we do routing here) */}
        {notInOnboardingFlow && (
          <>
            <AlertBars />
            {isCspAuthenticated && (
              <div
                css={css`
                  position: fixed;
                  border: 4px solid ${styleConstants.colors.yellow_dark};
                  top: 0px;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  z-index: 7;
                  pointer-events: none;
                `}
              />
            )}
            {isAgentBetaOrg && <AgentWrapper />}
            <Sidenav />
            <TopBar />
          </>
        )}
        {inLoadingState ? (
          <LoadingBackgroundPage />
        ) : (
          <>
            <Switch location={calculatedBackgroundLocation}>
              {updateAuthedBackgroundAppRoute.subRoutes.map(
                (route, i) =>
                  !route.exceptions?.some((exc) =>
                    matchPath(
                      calculatedBackgroundLocation.pathname,
                      exc.routeProps,
                    ),
                  ) && (
                    <Route key={i} {...route.routeProps}>
                      {route.component}
                    </Route>
                  ),
              )}
              {unauthedAppRoute.subRoutes.map((route, i) => (
                <Route key={i} {...route.routeProps}>
                  <Rdrct to={homeUrl()} />
                </Route>
              ))}
              <Route exact path={'/'}>
                <Rdrct to={homeUrl()} />
              </Route>
              <Route path={'/'}>
                <NotFoundBackgroundPage />
              </Route>
            </Switch>
            <Switch location={location}>
              {authedPopupAppRoute.subRoutes.map(
                (route, i) =>
                  !route.exceptions?.some((exc) =>
                    matchPath(location.pathname, exc.routeProps),
                  ) && (
                    <Route key={i} {...route.routeProps}>
                      {route.component}
                    </Route>
                  ),
              )}
            </Switch>
            <Switch location={location}>
              {authedLeftFlyoutAppRoute.subRoutes.map(
                (route, i) =>
                  !route.exceptions?.some((exc) =>
                    matchPath(location.pathname, exc.routeProps),
                  ) && (
                    <Route key={i} {...route.routeProps}>
                      {route.component}
                    </Route>
                  ),
              )}
            </Switch>
            <Switch location={location}>
              {authedRightFlyoutAppRoute.subRoutes.map(
                (route, i) =>
                  !route.exceptions?.some((exc) =>
                    matchPath(location.pathname, exc.routeProps),
                  ) && (
                    <Route key={i} {...route.routeProps}>
                      {route.component}
                    </Route>
                  ),
              )}
            </Switch>
            {isSessionExpiringPopupOpen && <SessionExpiringPopup />}
            {isOrgOwnerReassignmentPopupOpen && <OrgOwnerReassignmentPopup />}
            {isTooltipPopupOpen && <TooltipPopup />}
            {isAttachmentPopupOpen && <AttachmentPopup />}
            {isSendReceiptReminderPopupOpen && <SendReceiptReminderPopup />}
            {isCancelOrgInvitePopupOpen && <CancelOrgInvitePopup />}
            {isCreateOrgIntegrationOpen && <CreateOrgIntegration />}
            {isDeleteAccountPopOpen && <DeleteAccountPopup />}
            {coBrandWelcomePopupOpen &&
              isBrandSpecificWelcomePopupOpen &&
              notInOnboardingFlow &&
              coBrandName !== undefined &&
              showPncCoBrand && (
                <CoBrandWelcomePopup
                  cookieFunction={setCoBrandWelcomePopupOpen}
                  popupOpen={coBrandWelcomePopupOpen}
                  brandName={coBrandName}
                />
              )}
            {isCreateExpenseReportPopupOpen && <CreateExpenseReportPopup />}
            {isMarkExpenseReportsPaidPopupOpen && (
              <MarkExpenseReportsPaidPopup />
            )}
            {isDeleteExpensePopupOpen && <DeleteExpensePopup />}
            {isDeleteExpenseReportsPopupOpen && <DeleteExpenseReportsPopup />}
            {isDownloadExpenseReportsPaidPopupOpen && (
              <DownloadExpenseReports />
            )}
            {isDownloadSuccessPopupOpen && <DownloadSuccessPopup />}
            {isSetupOrganizationPopupOpen && <SetupOrganization />}
            {isSubmitExpenseReportPopupOpen && <SubmitExpenseReportPopup />}
            {isSyncTrasactionsPopupOpen && <SyncTransactionsPopup />}
            {isAddMissingInformationExpenseReportPopupOpen && (
              <AttachMissingInformationExpenseReportPopup />
            )}
            {isUploadReceiptsAutoMatchPopupOpen && (
              <UploadReceiptsAutoMatchPopup />
            )}
            {isRemoveBudgetUserPopupOpen && <RemoveBudgetUserPopup />}
            {isRequestCompletedPopupOpen && <RequestCompletedPopup />}
            {isBulkDeactivateVirtualCardsPopupOpen && (
              <BulkDeactivateVirtualCardsPopup />
            )}
            {isBulkCloseVirtualCardsPopupOpen && <BulkCloseVirtualCardsPopup />}
            {isBulkUpdateVirtualCardsFlyoutOpenFromState && (
              <BulkUpdateVirtualCardFlyout />
            )}
            {isAddSecondaryEmailPopupOpen && <AddSecondaryEmailPopup />}
            {confirmOrgExpenseReportDateChangePopupProps && (
              <ConfirmOrgExpenseReportDateChangePopup
                {...confirmOrgExpenseReportDateChangePopupProps}
              />
            )}
            {isChoose2faMethodPopupOpen && <Choose2faMethodPopup />}
            {isCreateDepartmentPopupOpen && (
              <CreateDepartmentPopup
                closeCreateDepartmentForm={(): void =>
                  dispatch(createSetIsAddDeptPopupOpen(false))
                }
              />
            )}
            {isAPIKeyCreatedPopupOpen && (
              <APIKeyCreatedPopup
                closeAPIKeyCreatedForm={(): void =>
                  dispatch(createSetIsAPIKeyCreatedPopupOpen(false, undefined))
                }
              />
            )}
            {isDeleteAPIKeyPopupOpen && (
              <DeleteAPIKeyPopup
                closeDeleteAPIKey={(): void =>
                  dispatch(createSetIsDeleteAPIKeyPopupOpen(false))
                }
              />
            )}
            {isBrandRedirectPopupOpen && <BrandRedirectPopup />}
            <Toast />
          </>
        )}
        {env !== Environments.Prod && (
          <div
            css={css`
              position: fixed;
              bottom: ${offSetDevTools ? css`85px` : css`5px`};
              right: 5px;
              transform: scale(0.4);
              transform-origin: right bottom;
              z-index: 999;
            `}
          >
            <DevToolsButton />
          </div>
        )}
      </div>
      <script>{/* needed for chatbot for some reason */}</script>
    </div>
  )
}

// TODO: this is NOT NECESSARY but for some reason useSelector returns a stale value,
// even when checking the values in the equality comparison and ensuring that a === b is false
export default connect(
  (state: RootState) => ({
    bgl: selectCalculatedBackgroundLocation(state),
  }),
  {},
)(Authed)
